"use client";

import { signIn, signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { ShieldOff, UserCog } from "lucide-react";
import {
  checkImpersonationGate,
  prepareImpersonation,
} from "@/actions/impersonation";
import { ImpersonationBanner } from "./impersonation-banner";

export function StaffLanding() {
  const { data: session, update } = useSession();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState(false);
  // A refused sign-in (e.g. attempting to impersonate a staff account)
  // bounces back here as /?error=<message>. The login page shows that
  // param inline, and this page must too: a mount-time toast is dropped
  // because sonner only delivers toasts to already-subscribed <Toaster>s,
  // and the root layout's Toaster (a later sibling of the page) has not
  // subscribed yet when this component's effects run. That left refusals
  // bouncing back here with no visible explanation.
  const authError = useSearchParams().get("error");
  const [error, setError] = useState<string | null>(authError);
  const [vpnGate, setVpnGate] = useState<{
    allowed: boolean;
    ip: string | null;
  } | null>(null);

  useEffect(() => {
    void checkImpersonationGate().then(setVpnGate);
  }, []);

  // Strip the param so a refresh doesn't re-show a stale message.
  useEffect(() => {
    if (!authError) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    window.history.replaceState(null, "", url);
  }, [authError]);

  const isImpersonating = Boolean(session?.impersonatedBy);
  const staffEmail = session?.impersonator?.email ?? session?.user?.email;
  const impersonatedEmail = isImpersonating ? session?.user?.email : null;

  const handleImpersonate = async () => {
    setLoading(true);
    setError(null);
    const result = await prepareImpersonation();
    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }
    await signIn("whmcs", { callbackUrl: "/" });
  };

  const handleStopImpersonating = async () => {
    setStopping(true);
    setError(null);
    try {
      await update({ stopImpersonation: true });
      router.replace("/");
      router.refresh();
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <ImpersonationBanner />
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 text-center">
        <UserCog className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-bold tracking-tight">
          Support staff
        </h1>
        {isImpersonating ? (
          <p className="mt-2 text-muted-foreground">
            You are impersonating <strong>{impersonatedEmail}</strong> as staff{" "}
            <strong>{staffEmail}</strong>, but that account owns no products, so
            there is nothing to show. You may have picked the wrong client in
            WHMCS: stop impersonating, click &ldquo;Login as Owner&rdquo; for
            the intended customer in WHMCS, and try again.
          </p>
        ) : (
          <p className="mt-2 text-muted-foreground">
            Signed in as <strong>{staffEmail}</strong>. To assist a customer,
            click &ldquo;Login as Owner&rdquo; for that customer in WHMCS first,
            then continue below.
          </p>
        )}
        {error && (
          <p className="mt-4 w-full rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="mt-6 flex w-full flex-col gap-2">
          {isImpersonating && (
            <button
              type="button"
              onClick={handleStopImpersonating}
              disabled={stopping}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {stopping ? "Stopping…" : "Stop impersonating"}
            </button>
          )}
          {!isImpersonating && vpnGate?.allowed && (
            <button
              type="button"
              onClick={handleImpersonate}
              disabled={loading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading
                ? "Redirecting…"
                : "Continue as currently impersonated customer (WHMCS)"}
            </button>
          )}
          {!isImpersonating && vpnGate && !vpnGate.allowed && (
            <p className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Impersonation is only available from the company VPN. Your current
              IP <strong>{vpnGate.ip ?? "(unknown)"}</strong> is not on the
              allowlist.
            </p>
          )}
          {!isImpersonating && (
            <Link
              href="/staff/reset-2fa"
              className="flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              <ShieldOff className="h-4 w-4" />
              Reset a user&apos;s 2FA
            </Link>
          )}
          <button
            type="button"
            onClick={() => signOut({ callbackUrl: "/" })}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
